// Appearance section — same controls as the floating Tweaks panel,
// mirrored here so users who don't know the FAB exists can still
// theme the app. Both surfaces share the same `useTweaks` state, so
// changes here flow through immediately.

import { ACCENT_OPTIONS, type Tweaks } from "../../lib/use-tweaks";

interface SettingsAppearanceProps {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
}

const DENSITIES: Tweaks["density"][] = ["compact", "regular", "comfy"];
const TYPES: { id: Tweaks["type"]; label: string; sub: string }[] = [
  { id: "geist",     label: "Geist",     sub: "Default — Geist sans + serif accents" },
  { id: "editorial", label: "Editorial", sub: "Bricolage display + serif body" },
  { id: "mono",      label: "Mono",      sub: "Geist Mono everywhere" },
];
const BUBBLES: { id: Tweaks["bubble"]; label: string }[] = [
  { id: "rounded", label: "Rounded" },
  { id: "square",  label: "Square" },
  { id: "minimal", label: "Minimal" },
];

export function SettingsAppearance({ tweaks, setTweak }: SettingsAppearanceProps) {
  return (
    <div className="settings-section">
      <h3 className="settings-section-title">Appearance</h3>
      <p className="settings-section-sub">
        Persisted locally for now — will sync to the server once preferences land.
      </p>

      <Row label="Theme">
        <div className="settings-chip-row">
          <Chip active={tweaks.dark} onClick={() => setTweak("dark", true)}>Dark</Chip>
          <Chip active={!tweaks.dark} onClick={() => setTweak("dark", false)}>Light</Chip>
        </div>
      </Row>

      <Row label="Accent">
        <div className="settings-accent-row">
          {ACCENT_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              className="settings-accent-swatch"
              data-active={tweaks.accent === c}
              onClick={() => setTweak("accent", c)}
              style={{ background: c }}
              aria-label="Accent color"
            />
          ))}
        </div>
      </Row>

      <Row label="Density">
        <div className="settings-chip-row">
          {DENSITIES.map((d) => (
            <Chip key={d} active={tweaks.density === d} onClick={() => setTweak("density", d)}>
              {d}
            </Chip>
          ))}
        </div>
      </Row>

      <Row label="Typography">
        <div className="settings-type-list">
          {TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              className="settings-type-row"
              data-active={tweaks.type === t.id}
              onClick={() => setTweak("type", t.id)}
            >
              <div className="settings-type-label">{t.label}</div>
              <div className="settings-type-sub">{t.sub}</div>
            </button>
          ))}
        </div>
      </Row>

      <Row label="Chat bubble">
        <div className="settings-chip-row">
          {BUBBLES.map((b) => (
            <Chip key={b.id} active={tweaks.bubble === b.id} onClick={() => setTweak("bubble", b.id)}>
              {b.label}
            </Chip>
          ))}
        </div>
      </Row>

      <Row label="Layout">
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={tweaks.sidebarDense}
            onChange={(e) => setTweak("sidebarDense", (e.currentTarget as HTMLInputElement).checked)}
          />
          Dense sidebar
        </label>
      </Row>

      <Row label="Chat">
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={tweaks.showToolCalls}
            onChange={(e) => setTweak("showToolCalls", (e.currentTarget as HTMLInputElement).checked)}
          />
          Show tool calls
        </label>
      </Row>

      <Row label="Diagnostics">
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={tweaks.debugTelemetry}
            onChange={(e) => setTweak("debugTelemetry", (e.currentTarget as HTMLInputElement).checked)}
          />
          Debug telemetry
        </label>
      </Row>
    </div>
  );
}

function Row({ label, children }: { label: string; children: any }) {
  return (
    <div className="settings-row">
      <label className="settings-row-label">{label}</label>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button type="button" className="settings-chip" data-active={active} onClick={onClick}>
      {children}
    </button>
  );
}
